<template>
  <div
    class="hero-banner"
    :class="{ [$class('active')]: startedPlaying }"
  >
    <splide
      ref="splide"
      class="hero-banner__carousel"
      :class="{ 'single-slide': (storeDataBlock || blocks).length === 1 }"
      :has-track="false"
      :options="localConfig"
      @splide:moved="handleSplideMoved"
    >
      <splide-track>
        <splide-slide
          v-for="(media, index) of (storeDataBlock || blocks)"
          :key="index"
        >
          <div
            class="hero-banner__content"
            :class="[
              isMobile ? settings.heightMobile : settings.heightDesktop,
              {
                'hero-banner__content--video': isMobile ? media.mobileVideo : media.desktopVideo
              },
            ]"
          >
            <div
              class="hero-banner__image-wrapper"
              :class="{
                'hero-banner__video-wrapper': isMobile ? media.mobileVideo : media.desktopVideo,
                'desktop-gradient': !isMobile && media.enableDesktopGradient,
                'mobile-gradient': isMobile && media.enableMobileGradient,
              }"
              :style="getGradient(media)"
            >
              <a
                v-if="media.bannerLink"
                class="hero-banner__link"
                :class="{
                  'hero-banner__link--video': isMobile ? media.mobileVideo : media.desktopVideo,
                }"
                :href="$localizeUrl(media.bannerLink)"
                v-text="media.title"
              />

              <template v-if="isMobile ? !media.mobileVideo : !media.desktopVideo">
                <responsive-image
                  v-if="!isMobile && media.desktopImage"
                  class="hero-banner__image desktop"
                  :image="getImage(media.desktopImage)"
                  :loaded-animation="false"
                  :lazyload="false"
                  :padding="settings.heightDesktop === 'image-height-first'"
                  ratio="image"
                  sizes="100vw"
                />

                <responsive-image
                  v-else-if="media.mobileImage || media.desktopImage"
                  class="hero-banner__image mobile"
                  :image="getImage(media.mobileImage || media.desktopImage)"
                  :loaded-animation="false"
                  :lazyload="false"
                  :padding="settings.heightMobile === 'image-height-first'"
                  ratio="image"
                  sizes="100vw"
                />
              </template>

              <template v-else>
                <video-player
                  v-if="!isMobile && media.desktopVideo"
                  class="hero-banner__video desktop"
                  :controls="{
                    hide: true,
                    id: `${liquid.sectionId}-${index}-desktop`,
                    muteButton: media.videoAudioControls,
                  }"
                  :image="media.desktopVideo.preview_image"
                  :intersection-observer="{
                    enable: getAutoPlaySettings(
                      settings.videoAutoPlay, media.videoAutoPlay)
                  }"
                  :is-active="settings.videoAutoPlay"
                  :is-dark="media.videoControlsColorDesktop === 'dark'"
                  :ratio="settings.heightDesktop === 'image-height-first' ? 'image' : false"
                  :sources="media.desktopVideo.sources"
                  :poster="media.videoPoster"
                  preload
                  @video-state-update="handleStateUpdate"
                />

                <video-player
                  v-else-if="media.mobileVideo"
                  class="hero-banner__video mobile"
                  :controls="{
                    id: `${liquid.sectionId}-${index}-mobile`,
                    muteButton: media.videoAudioControls,
                  }"
                  :image="media.mobileVideo.preview_image"
                  :intersection-observer="{
                    enable: getAutoPlaySettings(
                      settings.videoAutoPlay, media.videoAutoPlay)
                  }"
                  :is-active="settings.videoAutoPlay"
                  :is-dark="media.videoControlsColorMobile === 'dark'"
                  :ratio="false"
                  :sources="media.mobileVideo.sources"
                  :poster="media.mobileVideoPoster"
                  preload
                  @video-state-update="handleStateUpdate"
                />
              </template>
            </div>

            <div
              class="hero-banner__text-button-wrapper"
              :class="media.contentPosition"
            >
              <component
                :is="titleTag(media)"
                v-if="!media.products?.length && media.title"
                :href="titleTag(media) === 'a' ? media.titleLink : null"
                class="hero-banner__title hero-banner__title--wrapper"
                :class="[
                  `${media.titleFontDesktop}
                  ${media.titleFontMobile}
                  ${media.textColor}
                  ${media.titleFontWeightDesktop}
                  ${media.titleFontWeightMobile}
                  ${media.titleFontFamily}`,
                ]"
                v-text="media.title"
              />

              <p
                v-if="!media.products?.length && media.subheading"
                class="hero-banner__subheading"
                :class="[
                  `${media.subheadingFontDesktop}
                   ${media.subheadingFontMobile}
                   ${media.subheadingFontWeightDesktop}
                   ${media.subheadingFontWeightMobile}
                   ${media.subheadingColor}`,
                  {
                    'hero-banner__subheading-wrapper': (media.firstCtaTitle && media.firstCtaUrl) ||
                      (media.secondCtaTitle && media.secondCtaUrl),
                  }
                ]"
                v-text="media.subheading"
              />

              <div
                v-if="
                  !media.products?.length &&
                    (media.firstCtaTitle && media.firstCtaUrl) ||
                    (media.secondCtaTitle && media.secondCtaUrl)
                "
                class="hero-banner__ctas-wrapper"
              >
                <btn
                  v-if="media.firstCtaTitle && media.firstCtaUrl"
                  class="hero-banner__button"
                  :class="{ 'transparent': media.firstCtaType === 'outline'}"
                  :label="media.firstCtaTitle"
                  type="button"
                  :modifiers="getButtonModifiers(media.firstCtaType, media.firstCtaColor)"
                  :url="media.firstCtaUrl"
                />

                <btn
                  v-if="media.secondCtaTitle && media.secondCtaUrl"
                  class="hero-banner__button"
                  :class="{ 'transparent': media.secondCtaType === 'outline'}"
                  :label="media.secondCtaTitle"
                  type="button"
                  :modifiers="getButtonModifiers(media.secondCtaType, media.secondCtaColor)"
                  :url="media.secondCtaUrl"
                />
              </div>

              <div
                v-if="media.products?.length"
                class="hero-banner__hotspot-cta-wrapper"
              >
                <button
                  class="
                  hero-banner__hotspot-cta
                  shoppable-hotspot-cta
                "
                  type="button"
                  @click.prevent="hotspotShowingIndex = index"
                >
                  <icon-cart />

                  <span
                    class="
                    shoppable-hotspot-cta__label
                    text-body-2xs-regular
                  "
                    v-text="$string('lookbook.shop_the_look')"
                  />
                </button>
              </div>
            </div>

            <div
              v-show="hotspotShowingIndex === index"
              v-if="media.products?.length"
              class="hero-banner__hotspot-wrapper"
            >
              <shoppable-hotspot
                :products="media.products"
                :product-card-location="{
                  key: 'sectionHotspot',
                  metaobjectName: ''
                }"
                show-backdrop
                :active="hotspotShowingIndex === index"
                limited-width
                @closed="hideHotspot"
              />
            </div>
          </div>
        </splide-slide>
      </splide-track>

      <div
        ref="splideArrows"
        class="hero-banner__arrows splide__arrows"
        :class="{
          'has-bg': settings.arrowBg
        }"
      >
        <button
          class="splide__arrow splide__arrow--prev"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.previous')"
          />

          <icon-arrow-left />
        </button>

        <button
          class="splide__arrow splide__arrow--next"
          type="button"
        >
          <span
            class="visually-hidden"
            v-text="$string('accessibility.carousel.next')"
          />

          <icon-arrow-right />
        </button>
      </div>
    </splide>
  </div>
</template>

<script>

/**
 * Vue: Hero banner (hero-banner)
 * -----------------------------------------------------------------------------
 * Hero banner section with image and video options.
 *
 * @param {Array} blocks - Section blocks.
 * @param {Object} liquid - Liquid settings.
 * @param {Object} settings = Section Settings
 *
 */
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'
import { mapGetters, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import VideoPlayer from '~async/video-player/video-player'
import ShoppableHotspot from '~async/shoppable-hotspot/shoppable-hotspot'

import IconArrowLeft from '~icons/directional-navigation/chevron/left.svg'
import IconArrowRight from '~icons/directional-navigation/chevron/right.svg'
import IconCart from '~icons/general/cart.svg'
import { storeLocatorProps } from '../store-locator-map/helpers'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'HeroBanner',

  components: {
    Btn,
    IconArrowLeft,
    IconArrowRight,
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    VideoPlayer,
    ShoppableHotspot,
    IconCart,
  },

  props: {
    blocks: {
      type: Array,
      default: () => [],
    },
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
      }),
    },
    settings: {
      type: Object,
      default: () => ({}),
    },
    uberallStoreId: {
      type: Number,
      default: null,
    },
  },

  data() {
    return {
      arrowColor: 'light',
      breakpointL: breakpoints.l,
      currentIndex: 0,
      localConfig: {},
      startedPlaying: false,
      hotspotShowingIndex: -1,
      storeDataBlock: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      activeOverlay: 'overlays/getActiveOverlay',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  watch: {

    /**
     * Watch active overlay to nudge carousel.
     */
    async activeOverlay() {
      await this.$nextTick()
      this.goToIndex(this.currentIndex)
    },
  },

  beforeMount() {
    this.localConfig = {
      arrows: this.blocks.length > 1,
      autoplay: this.settings.isRotating && !this.videoSlidesExist(),
      direction: 'ltr',
      drag: this.blocks.length > 1,
      fixedWidth: '100%',
      interval: this.settings.rotateDelay * 1000,
      pagination: false,
      pauseOnHover: false,
      perPage: 1,
      speed: this.$timing('slowest'),
      start: 0,
      type: this.blocks.length === 1 ? 'slide' : 'loop',
      arrowBg: this.settings.arrowBg,
    }

    if (
      !this.blocks?.length ||
      (!this.blocks?.[0]?.desktopVideo &&
        !this.blocks?.[0]?.mobileVideo)
    ) {
      this.startedPlaying = true
    }
  },

  mounted() {
    this.splideMoveListen()
    this.checkStoreLocatorDetails()
  },

  methods: {

    /**
     * Get autoplay settings
     * @param {Boolean} globalSetting - Global autoplay setting
     * @param {Boolean} blockSetting - Block autoplay setting
     * @returns {Boolean}
     */
    getAutoPlaySettings(globalSetting, blockSetting) {
      if (globalSetting || blockSetting) {
        return true
      }

      return false
    },

    /**
     * Checking if video slide exists
     */
    videoSlidesExist() {
      return this.blocks.some((media) => {
        return this.isMobile ? media.mobileVideo : media.desktopVideo
      })
    },

    /**
     * Get button modifiers
     * @param {String} type - Button type
     * @param {String} color - Button color
     */
    getButtonModifiers(type, color) {
      if (color === 'light') {
        return `${type} light`
      }

      return `${type} dark`
    },

    /**
      * Get image
      */
    getImage(image) {
      if (image.src) {
        return image
      }
      return {
        src: image,
      }
    },

    /**
     * Sets banner title tag per link provided
     */
    titleTag(bannerData) {
      if (bannerData.titleLink &&
        !bannerData.firstCtaTitle &&
        !bannerData.firstCtaUrl &&
        !bannerData.secondCtaTitle &&
        !bannerData.secondCtaUrl) {
        return 'a'
      }

      return 'p'
    },

    /**
     * Change arrow color on splide scroll
     */
    splideMoveListen() {
      const splide = this.$refs.splide
      const arrows = this.$refs.splideArrows

      if (!splide) {
        return
      }

      if (this.blocks?.[0]?.arrowColor === 'dark') {
        arrows.classList.add('dark')
        arrows.classList.add('light-bg')
      }

      splide.splide.on('moved', (newIndex) => {
        const arrowColor = this.blocks?.[newIndex].arrowColor

        if (arrowColor === 'dark') {
          arrows.classList.add('dark')
          arrows.classList.add('light-bg')
        } else {
          arrows.classList.remove('dark')
          arrows.classList.remove('light-bg')
        }
      })
    },

    /**
     * Handle state update.
     * - Fades in section after video has started.
     * @param {String} state - Video state.
     */
    async handleStateUpdate(state) {
      if (!['ready', 'playing'].includes(state)) {
        return
      }

      /**
       * Arbitrary delay before fading in so video has started.
       */
      await cnvs.Sleep(400)
      this.startedPlaying = true
    },

    /**
     * Handle Splide moved.
     * @param {Object} splide - Splide instance.
     * @param {Number} newIndex - New slide index.
     */
    handleSplideMoved(_, newIndex) {
      this.currentIndex = newIndex
    },

    /**
     * Go to slide.
     * - Sets speed to 0 to instantly update slide without animation.
     * @param {Number} index - Slide index to go to.
     */
    goToIndex(index) {
      if (!this.$refs.splide) {
        return
      }

      this.localConfig.speed = 0

      setTimeout(() => {
        this.$refs.splide.go(index)
      }, 0)

      setTimeout(() => {
        this.localConfig.speed = this.$timing('quick')
      }, 10)
    },

    /**
     * Adds gradient over hero banner
     */
    getGradient(block) {
      let gradientStyle = ''
      if (block.enableDesktopGradient && !this.isMobile) {
        gradientStyle += `--desktop-gradient: ${block.desktopGradient}`
      }

      if (block.enableMobileGradient && this.isMobile) {
        gradientStyle += `--mobile-gradient: ${block.mobileGradient}`
      }

      return gradientStyle
    },

    /**
     * Hide hotspot
     */
    hideHotspot() {
      this.hotspotShowingIndex = -1
    },

    /**
     * Check if hero is to use store banner
     */
    checkStoreLocatorDetails() {
      if (this.settings.useStoreData && this.uberallStoreId) {
        this.getStoreBannerImage()
      }
    },

    /**
     * Get store banner image
     */
    async getStoreBannerImage() {
      const options = {
        method: 'GET',
        headers: {
          accept: 'application/json',
        },
      }
      const locationResults = await fetch(
        `${storeLocatorProps.activeEnvironmentUrl}${this.uberallStoreId}${storeLocatorProps.storePageBannerField}`,
        options,
      ).then(response => response.json())
      const storeSecondaryImage = locationResults.response?.customFields?.[0].value

      if (storeSecondaryImage) {
        this.storeDataBlock = [
          {
            desktopImage: {
              src: storeSecondaryImage,
            },
          },
        ]
      }
    },
  },
}
</script>

<style lang="scss">
@import './hero-banner';
</style>
